import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { getErrorMessage } from '@/shared/api/api-error';

import {
  createRegion,
  deleteRegion,
  getRegionOptions,
  getRegionOrder,
  getRegions,
  saveRegionOrder,
  setRegionStatus,
  updateRegion,
} from './regions.api';
import type { RegionListParams, RegionPayload } from '../types/region.types';

/**
 * Query keys. A change invalidates every region query and the brands feature,
 * whose Brand Details show region names and "region inactive" reasons.
 */
export const regionKeys = {
  all: ['regions'] as const,
  lists: () => [...regionKeys.all, 'list'] as const,
  list: (params: RegionListParams) => [...regionKeys.lists(), params] as const,
  options: () => [...regionKeys.all, 'options'] as const,
  order: () => [...regionKeys.all, 'order'] as const,
};

/** The brands feature's root key — kept literal to avoid importing that feature. */
const BRANDS_KEY = ['brands'] as const;

export function useRegions(params: RegionListParams) {
  return useQuery({
    queryKey: regionKeys.list(params),
    queryFn: ({ signal }) => getRegions(params, signal),
    placeholderData: (previous) => previous,
  });
}

/** Region options for pickers; small and curated, so cached for a while. */
export function useRegionOptions() {
  return useQuery({
    queryKey: regionKeys.options(),
    queryFn: ({ signal }) => getRegionOptions(signal),
    staleTime: 15 * 60_000,
  });
}

/**
 * The storefront region order, exactly as the backend returns it. Refetched
 * every time the dialog opens: another admin may have saved an order since.
 */
export function useRegionOrder() {
  return useQuery({
    queryKey: regionKeys.order(),
    queryFn: ({ signal }) => getRegionOrder(signal),
    staleTime: 0,
  });
}

/** Errors (a stale or incomplete list) are shown in the dialog by the caller. */
export function useSaveRegionOrder() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateRegions();
  return useMutation({
    mutationFn: ({ regionIds, reason }: { regionIds: number[]; reason: string }) =>
      saveRegionOrder(regionIds, reason),
    onSuccess: (changed) => {
      toast.success(
        changed === 0 ? 'The order was already saved.' : 'Storefront region order saved.',
      );
      // The list, the pickers and every Brand Details (regions in store order) change.
      invalidate();
    },
    // A 409 means a region was added or removed meanwhile — reload the order.
    onError: () => void queryClient.invalidateQueries({ queryKey: regionKeys.order() }),
  });
}

function useInvalidateRegions() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: regionKeys.all });
    void queryClient.invalidateQueries({ queryKey: BRANDS_KEY });
  };
}

export function useCreateRegion() {
  const invalidate = useInvalidateRegions();
  return useMutation({
    mutationFn: (payload: RegionPayload) => createRegion(payload),
    onSuccess: (_id, payload) => {
      toast.success(`Region "${payload.name}" created.`);
      invalidate();
    },
    // Field errors (a code in use) are mapped onto the form by the caller.
  });
}

export function useUpdateRegion() {
  const invalidate = useInvalidateRegions();
  return useMutation({
    mutationFn: ({ regionId, payload }: { regionId: number; payload: RegionPayload }) =>
      updateRegion(regionId, payload),
    onSuccess: (_result, { payload }) => {
      toast.success(`Region "${payload.name}" updated.`);
      invalidate();
    },
  });
}

/** Only an unused, non-system region can be deleted; the API's refusal is shown as a toast. */
export function useDeleteRegion() {
  const invalidate = useInvalidateRegions();
  return useMutation({
    mutationFn: (input: { regionId: number; name: string; reason: string | null }) =>
      deleteRegion(input.regionId, input.reason),
    onSuccess: (_result, { name }) => {
      toast.success(`Region "${name}" deleted.`);
      invalidate();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not delete the region.'));
      // A 409 means denominations were added meanwhile — refresh the counts.
      invalidate();
    },
  });
}

/** No optimistic update: deactivating a region changes what customers can buy. */
export function useSetRegionStatus() {
  const invalidate = useInvalidateRegions();
  return useMutation({
    mutationFn: (input: { regionId: number; isActive: boolean; reason: string | null }) =>
      setRegionStatus(input.regionId, input.isActive, input.reason),
    onSuccess: (changed, { isActive }) => {
      toast.success(
        changed === 0
          ? 'Nothing to change.'
          : isActive
            ? 'Region activated.'
            : 'Region deactivated.',
      );
      invalidate();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not change the region.'));
    },
  });
}
